/**
 * COLGROUP-4 — jump bar decoration.
 *
 * The bar in `snippets/collection-groups-nav.liquid` is plain anchors inside a `position: sticky`
 * nav: it sticks and it jumps with this file absent or blocked. Two things happen here —
 *
 *   1. `aria-current="true"` on the pill whose group is in view (IntersectionObserver, no polling).
 *   2. `--colgroup-nav-height` published from the bar's measured height, so a tapped pill lands
 *      with the group's heading clear of the bar instead of underneath it.
 *
 * The bar used to slide away on a phone while scrolling down. The CEO found that confusing on the
 * live page and asked for it to stay put, so hide-on-scroll and its `decideBarHidden` decision are
 * gone rather than merely switched off — a bar that is always there needs no rule about when it is
 * not. The bar stays visible at every width.
 *
 * No library, no bundler. The remaining decision is a pure function so it can be tested in Node
 * without a DOM (`tests/colgroup-nav.test.mjs`); the DOM wiring below is the thin part.
 */

/** Custom property the section's `scroll-margin-top` reads. */
export const NAV_HEIGHT_PROPERTY = '--colgroup-nav-height';

/**
 * The bar's height as a CSS length, rounded **up**.
 *
 * Up, not to-nearest, on purpose. This value only ever buys clearance under a sticky bar, so a
 * spare pixel is invisible whereas a missing one clips the heading — which is exactly how the
 * theme's own `Math.round` on `--header-height` left a visible seam (COLGROUP-5, decision 37).
 *
 * @param {number} height measured height in pixels.
 * @returns {string} a CSS length.
 */
export function navHeightValue(height) {
  const safe = Number.isFinite(height) && height > 0 ? height : 0;
  return `${Math.ceil(safe)}px`;
}

/**
 * Which pill should read as current.
 *
 * Classic scroll-spy rule: once a group's heading has passed above the anchor line, that group is
 * the one being read, so the last group to have passed wins. Before any group has passed — at the
 * top of the page — the topmost group still on screen wins. When nothing is on screen at all
 * (a fast fling between observer callbacks) the previous pick is kept rather than clearing the
 * highlight and making the bar flicker.
 *
 * @param {Array<{index: number, ratio: number, top: number}>} states one entry per group section,
 *   `top` measured from the anchor line (negative once the section has scrolled past it) and
 *   `ratio` the fraction of it currently visible.
 * @param {number} [previousIndex] pick to keep when no section is visible.
 * @returns {number} index of the pill to mark current.
 */
export function pickActiveIndex(states, previousIndex = 0) {
  if (!Array.isArray(states) || states.length === 0) return previousIndex;

  const visible = states.filter((state) => state && state.ratio > 0);
  if (visible.length === 0) return previousIndex;

  const passed = visible.filter((state) => state.top <= 0);
  const chosen =
    passed.length > 0
      ? passed.reduce((best, state) => (state.top >= best.top ? state : best))
      : visible.reduce((best, state) => (state.top < best.top ? state : best));

  return chosen.index;
}

/**
 * Wire one jump bar to its group sections.
 *
 * @param {HTMLElement} nav the `[data-colgroup-nav]` element.
 * @returns {() => void} teardown, for tests and for a section re-render.
 */
export function initCollectionGroupsNav(nav) {
  const pills = Array.from(nav.querySelectorAll('[data-colgroup-pill]'));
  const sections = pills.map((pill) => document.getElementById(pill.dataset.colgroupPill));
  const teardowns = [];

  // 0. Publish the bar's height so the sections know how far to sit below it.
  const publishNavHeight = (height) => {
    document.documentElement.style.setProperty(NAV_HEIGHT_PROPERTY, navHeightValue(height));
  };
  publishNavHeight(nav.getBoundingClientRect().height);
  if (typeof ResizeObserver === 'function') {
    // The bar's height changes with the viewport (pills wrap, font scales), and it is measured
    // rather than assumed because the fallback in the stylesheet is only there for JS-off.
    const sizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const box = entry.borderBoxSize && entry.borderBoxSize[0];
      publishNavHeight(box ? box.blockSize : entry.contentRect.height);
    });
    sizeObserver.observe(nav);
    teardowns.push(() => sizeObserver.disconnect());
  }

  // 1. Highlight the group in view.
  const observed = sections.filter(Boolean);
  if (observed.length > 0 && typeof IntersectionObserver === 'function') {
    const states = sections.map((section, index) => ({ index, ratio: 0, top: Infinity, section }));
    let activeIndex = -1;

    const setActive = (index) => {
      if (index === activeIndex) return;
      activeIndex = index;
      pills.forEach((pill, i) => {
        if (i === index) pill.setAttribute('aria-current', 'true');
        else pill.removeAttribute('aria-current');
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const state = states.find((candidate) => candidate.section === entry.target);
          if (!state) continue;
          // Measured from the observer's anchor line, not the viewport top, so `top <= 0` means
          // "this group has been scrolled past". `rootBounds` is null in a cross-origin frame.
          const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
          state.ratio = entry.intersectionRatio;
          state.top = entry.boundingClientRect.top - rootTop;
        }
        setActive(pickActiveIndex(states, Math.max(activeIndex, 0)));
      },
      { threshold: [0, 0.01, 0.5, 1], rootMargin: '-25% 0px -60% 0px' }
    );

    observed.forEach((section) => observer.observe(section));
    teardowns.push(() => observer.disconnect());
    setActive(0);
  }

  return () => teardowns.forEach((teardown) => teardown());
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-colgroup-nav]').forEach((nav) => initCollectionGroupsNav(nav));
}
